import {Component, ElementRef, Input, OnDestroy, ViewChild} from '@angular/core';
import {
  StatusEventPageEditConfigurationComponent
} from '@components/pages/page-components/status-event-page/modals/status-event-page-edit-configuration/status-event-page-edit-configuration.component';
import {
  StatusEventPageCloneComponent
} from '@components/pages/page-components/status-event-page/modals/status-event-page-clone/status-event-page-clone.component';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {TranslateService} from '@ngx-translate/core';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {StatusEventPageService} from '@services/pages/status-event-page/configuration/status-event-page.service';
import {StatusEventType} from '@constants/machine/status-event-signal/status-event-type.enum';
import {
  StatusEventPageActionsParameters
} from '@models/machine/status-event-signal/status-event-page-actions-parameters';
import {
  StatusEventPageActionsQueueService
} from '@services/pages/status-event-page/action-events-queue/status-event-page-actions-queue.service';
import {StatusEventSignalEventsDetails} from '@models/machine/status-event-signal/status-event-signal-events-details';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {TemplatePagesService} from '@services/left-menu/page/template-user-defined-page/template-pages.service';
import {takeUntil} from 'rxjs/operators';
import {Observable, Subject} from 'rxjs';
import {TemplateStatusPageSignalDetails} from '@models/sidebar/request/template-page-configuration-details';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-status-event-page-actions',
    templateUrl: './status-event-page-actions.component.html',
    styleUrls: ['./status-event-page-actions.component.scss'],
    standalone: false
})
export class StatusEventPageActionsComponent implements OnDestroy {

  @Input()
  public pageName: string = '';

  @Input()
  public machineId: string | null = null;

  @Input()
  public machineTag: string | null = null;

  @Input()
  public machineCategory: string | null = null;

  @Input() set pageId(value: string | null) {
    this._pageId = value;
    this.statusEventPageActionsParameters.pageId = value;
    this.setAvailableSignals();
  }

  @Input()
  public organizationId: string | null = null;

  @Input()
  public isAdminOrHigher: boolean = false;

  @Input()
  public isAdminOrLower: boolean = false;

  @Input()
  public isTemplatePage: boolean = false;

  @Input() set statusEventPageConfiguration(value: any) {
    this._statusEventPageConfiguration = value;
    this.setAvailableSignals();
  }

  @Input()
  public syncTemplatePage: boolean = false;

  @Input()
  public isLoading: boolean = false;
  public availableStatusEventSignals: StatusEventSignalEventsDetails[] = [];
  public statusEventPageActionsParameters: StatusEventPageActionsParameters = new StatusEventPageActionsParameters('', [], [], false);


  public showErrorEvents: boolean = true;
  public showWarningEvents: boolean = true;
  public showInfoEvents: boolean = true;
  public showDebugEvents: boolean = true;
  public showInhibitEvents: boolean = true;

  @ViewChild('syncTemplate') syncTemplatePageCheckbox: ElementRef | undefined;
  private COMPONENT_NAME: string = 'StatusEventPageActionsComponent';
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private _pageId: string | null = null;
  private _statusEventPageConfiguration: StatusEventPage = new StatusEventPage(-1, '');


  constructor(private modalService: ModalService,
              private translate: TranslateService,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService,
              private statusEventPageService: StatusEventPageService,
              private statusEventPageActionsQueueService: StatusEventPageActionsQueueService,
              private leftMenuService: LeftMenuService,
              private templatePagesService: TemplatePagesService,
              private loadingIndicatorService: LoadingIndicatorService) {
  }

  ngOnDestroy(): void {
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  public onEditPage(): void {
    this.statusEventPageActionsParameters.isInConfigurationMode = true;
    this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);

    const modalRef = this.modalService.open(StatusEventPageEditConfigurationComponent, {size: 'lg'});
    modalRef.componentInstance.machineId = this.machineId;
    modalRef.componentInstance.pageConfiguration = this._statusEventPageConfiguration;

    modalRef.result.then((signals: StatusEventSignalEventsDetails[]) => {

      this.savePageConfiguration(this.machineId, this._pageId, signals);

    }, () => {
      this.handleModalCancel();
    });
  }

  public onCopyPage(): void {
    this.statusEventPageActionsParameters.isInConfigurationMode = true;
    this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);

    const modalRef = this.modalService.open(StatusEventPageCloneComponent, {size: 'lg'});
    modalRef.componentInstance.pageName = this.pageName;
    modalRef.componentInstance.machineId = this.machineId;
    modalRef.componentInstance.pageId = this._pageId;
    modalRef.componentInstance.organizationId = Number(this.organizationId);
    modalRef.result.then((selectedMachineIds: number[]) => {

      this.startEventPageConfigurationClone(this.machineId, this._pageId, this.isTemplatePage, selectedMachineIds);

      this.statusEventPageActionsParameters.isInConfigurationMode = false;
      this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);
      this.leftMenuService.resetCache();
    }, () => {
      this.handleModalCancel();
    });
  }

  onClearAll() {
    this.statusEventPageActionsParameters.visibleStatusEventSignals = [];
  }

  onSelectAll() {
    this.statusEventPageActionsParameters.visibleStatusEventSignals = [...this.availableStatusEventSignals.map(s => s.name)];
  }

  onErrorCheckboxChange($event: any) {
    if ($event?.target) {
      this.showErrorEvents = $event.target.checked;
    } else {
      this.showErrorEvents = false;
    }
  }

  onWarningCheckboxChange($event: any) {
    if ($event?.target) {
      this.showWarningEvents = $event.target.checked;
    } else {
      this.showWarningEvents = false;
    }
  }

  onInfoCheckboxChange($event: any) {
    if ($event?.target) {
      this.showInfoEvents = $event.target.checked;
    } else {
      this.showInfoEvents = false;
    }
  }

  onDebugCheckboxChange($event: any) {
    if ($event?.target) {
      this.showDebugEvents = $event.target.checked;
    } else {
      this.showDebugEvents = false;
    }
  }

  onInhibitCheckboxChange($event: any) {
    if ($event?.target) {
      this.showInhibitEvents = $event.target.checked;
    } else {
      this.showInhibitEvents = false;
    }
  }

  onFilterByEventType() {
    const visibleEventTypes: StatusEventType[] = [];

    if (this.showErrorEvents) {
      visibleEventTypes.push(StatusEventType.FAILURE);
    }
    if (this.showWarningEvents) {
      visibleEventTypes.push(StatusEventType.WARNING);
    }
    if (this.showInfoEvents) {
      visibleEventTypes.push(StatusEventType.INFO);
    }
    if (this.showDebugEvents) {
      visibleEventTypes.push(StatusEventType.DEBUG);
    }
    if (this.showInhibitEvents) {
      visibleEventTypes.push(StatusEventType.INHIBIT);
    }
    this.statusEventPageActionsParameters.visibleStatusEventTypes = [...visibleEventTypes];
    this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);

  }

  public onApplySignalFilter() {

    this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);
  }

  public onSyncTemplatePage() {
    if (!this.syncTemplatePageCheckbox) {
      return;
    }
    this.syncTemplatePageCheckbox.nativeElement.checked = this.syncTemplatePage;
    const machineTag = this.machineTag ? this.machineTag.toBold() : this.machineTag;
    const machineCategory = this.machineCategory ? this.machineCategory.toBold() : this.machineCategory;
    const messageTitle = this.translate.instant('Enable/Disable synchronization');
    let messageBody = this.translate.instant('Enabling user template page synchronization will make this page available to all machines of tag #tag and category #category. Are you sure you want to proceed?')
    .replace('#tag', machineTag).replace('#category', machineCategory);
    if (this.syncTemplatePage) {
      messageBody = this.translate.instant('Disabling user template page synchronization will make this page available only to the parent machine of #tag and category #category. Are you sure you want to proceed?')
      .replace('#tag', machineTag).replace('#category', machineCategory);
    }

    const messageOK = this.translate.instant('Yes');
    const messageCancel = this.translate.instant('Cancel');
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = messageTitle;
    modalRef.componentInstance.body = messageBody;
    modalRef.componentInstance.okButtonText = messageOK;
    modalRef.componentInstance.cancelButtonText = messageCancel;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        return;
      } else {
        this.syncTemplatePage = !this.syncTemplatePage;
        this.setSynchronizationFlag();
      }
    });
  }

  private handleModalCancel() {
    this.statusEventPageActionsParameters.isInConfigurationMode = false;
    this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);
  }

  private savePageConfiguration(machineId: string | null, pageId: string | null, signals: StatusEventSignalEventsDetails[]) {
    if (!machineId || !pageId) {
      return;
    }
    this.isLoading = true;
    this.loadingIndicator.show();
    let observable: Observable<any>;
    if (this.isTemplatePage) {
      const signalDetails: TemplateStatusPageSignalDetails[] = this.parseConfigurationToSignalDetails(signals);
      observable = this.templatePagesService.saveStatusEventSignals(machineId, pageId, signalDetails);
    } else {
      const signalIds: number[] = signals.map(s => s.id);
      this._statusEventPageConfiguration.signals = [...signalIds];
      observable = this.statusEventPageService.update(machineId, pageId, this._statusEventPageConfiguration);
    }

    observable.pipe(takeUntil(this.$destroy)).subscribe({
          next: updatedPage => {
            [this._statusEventPageConfiguration, this.syncTemplatePage] = this.templatePagesService.parseTemplatePageToStatusPage(updatedPage, machineId, this.isTemplatePage);
            this.setAvailableSignals();
            let message = this.translate.instant('The status page %page configuration was updated successfully');
            message = message.replaceAll('%page', this.pageName.toBold());
            this.notification.success(message, this.COMPONENT_NAME, false);
            this.loadingIndicator.hide();
            this.isLoading = false;
            this.statusEventPageActionsParameters.isInConfigurationMode = false;
            this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);
          }, error: (error) => {
            let message = this.translate.instant('There was an error saving status page %page configuration');
            message = message.replaceAll('%page', this.pageName.toBold());
            this.notification.error(message, error, this.COMPONENT_NAME, true);
            this.loadingIndicator.hide();
            this.isLoading = false;
            this.statusEventPageActionsParameters.isInConfigurationMode = false;
            this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);
          }
        }
    );

  }

  private startEventPageConfigurationClone(machineId: string | null, pageId: string | null, isTemplatePage: boolean, targetMachines: number[]) {
    if (!machineId || !pageId) {
      return;
    }

    this.isLoading = true;
    this.loadingIndicator.show();
    this.statusEventPageService.startStatusEventPageCloningProcess(machineId, pageId, isTemplatePage, targetMachines)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
          next: () => {
            const message = this.translate.instant('The process of cloning status event page %page configuration was started successfully').replace('%page', this.pageName.toBold());

            this.notification.success(message, this.COMPONENT_NAME, false);
            this.loadingIndicator.hide();
            this.isLoading = false;
            this.statusEventPageActionsParameters.isInConfigurationMode = false;
            this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);
          }, error: (error) => {
            const message = this.translate.instant('There was an error starting the cloning process of status event page %page!').replace('%page', this.pageName.toBold());
            this.notification.error(message, error, this.COMPONENT_NAME, true);
            this.loadingIndicator.hide();
            this.isLoading = false;
            this.statusEventPageActionsParameters.isInConfigurationMode = false;
            this.statusEventPageActionsQueueService.dispatch(this.statusEventPageActionsParameters);
          }
        }
    );
  }

  private setSynchronizationFlag() {
    if (!this.machineId || !this._pageId) {
      return;
    }
    this.loadingIndicatorService.show();
    this.templatePagesService.setSynchronizationFlag(this.machineId, this._pageId, this.syncTemplatePage)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: () => {
        const message = this.syncTemplatePage ? this.translate.instant('Status event template page synchronization enabled') : this.translate.instant('User defined template page synchronization disabled');
        this.notification.success(message, this.COMPONENT_NAME, false);
        this.loadingIndicatorService.hide();
      }, error: error => {
        this.notification.error(this.translate.instant('Status event template page synchronization changed failed.'), error, this.COMPONENT_NAME, true);
        this.loadingIndicatorService.hide();
      }
    });
  }


  private parseConfigurationToSignalDetails(signals: StatusEventSignalEventsDetails[]): TemplateStatusPageSignalDetails[] {
    const result: TemplateStatusPageSignalDetails[] = [];
    if (!signals || signals.length === 0) {
      return result;
    }

    signals.forEach((value: StatusEventSignalEventsDetails) => {
      const item: TemplateStatusPageSignalDetails = new TemplateStatusPageSignalDetails();
      item.signalId = value.id;
      item.signalName = value.name;
      result.push(item);
    });

    return result;
  }

  private setAvailableSignals(): void {
    const statusEventSignalEventsDetails = Array.from(Object.values(this._statusEventPageConfiguration.statusEventsDetails));

    if (statusEventSignalEventsDetails && statusEventSignalEventsDetails.length > 0) {
      this.availableStatusEventSignals = [...statusEventSignalEventsDetails];
    } else {
      this.availableStatusEventSignals = [...Array.from(this._statusEventPageConfiguration.statusEventsDetails.values())];
    }
  }

}
